import { toolNoun, toolVerb } from './tools';
import { StoredTurn } from './types';

/**
 * The tool trace (chat-ui-spec §5.6–5.9): its items, the summary line for every state, and each
 * step's label. Live and restored turns build the same items, so one component draws both.
 */

export interface TraceStep {
  kind: 'step';
  i: number;
  tool: string;
  purpose: string;
  /** The SQL for `run_sql`, otherwise the search query. */
  code?: string;
  status: 'running' | 'done' | 'error';
  count: number;
  error?: string;
  /** Data URLs of what the agent looked at; live only, never stored. */
  images: string[];
  flow: string;
  flowTitle?: string;
  /** Part of a batch of several calls (never today: the server asks for one at a time). */
  parallel: boolean;
  batchStart: boolean;
}

export interface TraceThinking {
  kind: 'thinking';
  text: string;
}

export type TraceItem = TraceStep | TraceThinking;

export const PLANNING = 'Planning…';
export const THINKING = 'Thinking…';

const quantity = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/** `Step 3 of 12`, or `Step 3` when the budget is unknown. */
export const stepProgress = (i: number, maxSteps?: number): string =>
  maxSteps ? `Step ${i + 1} of ${maxSteps}` : `Step ${i + 1}`;

/** `Reasoning & tools — 2 queries, 1 search (1 retried)`; searches are every non-SQL step. */
export function finishedSummary(steps: Array<{ tool: string; error?: string | null }>): string {
  const queries = steps.filter((s) => s.tool === 'run_sql').length;
  const searches = steps.length - queries;
  const failed = steps.filter((s) => !!s.error).length;
  return `Reasoning & tools — ${quantity(queries, 'query', 'queries')}, ${quantity(
    searches,
    'search',
    'searches',
  )}${failed ? ` (${failed} retried)` : ''}`;
}

/** Kept as the original words it, even when the steps were searches (§11 quirk 6). */
export const failedSummary = (done: number): string =>
  `Reasoning & SQL — ${quantity(done, 'query', 'queries')} before failure`;

export const stoppedSummary = (done: number): string =>
  `Reasoning & SQL — ${quantity(done, 'query', 'queries')} before stopping`;

/** `purpose`, then `purpose — 3 rows` or `purpose — failed: …` once the step has settled. */
export function stepLabel(
  step: Pick<TraceStep, 'tool' | 'purpose' | 'status' | 'count' | 'error'>,
) {
  const base = step.purpose || toolVerb(step.tool) || 'Working';
  if (step.status === 'running') return base;
  if (step.status === 'error') return `${base} — failed: ${step.error ?? ''}`;
  const noun = toolNoun(step.tool);
  return `${base} — ${step.count} ${step.count === 1 ? noun : `${noun}s`}`;
}

/** Live counts prefer `matchCount`; restored ones prefer `rowCount` (§11 quirk 5). */
export const liveCount = (frame: { rowCount?: number; matchCount?: number }): number =>
  (frame.matchCount ?? frame.rowCount) || 0;

export const storedCount = (step: { rowCount: number | null; matchCount: number | null }): number =>
  step.rowCount ?? step.matchCount ?? 0;

export const chainFlow = (i: number) => ({
  flow: `↳ step ${i + 1}`,
  flowTitle: 'The model waited for the previous result before choosing this call.',
});

export const parallelFlow = (k: number, n: number) => ({
  flow: `⇉ ${k}/${n} parallel`,
  flowTitle: `The model requested ${n} tools in one response; they run together.`,
});

/** A stored turn's trace: reasoning before the first step, then each step and what followed it. */
export function restoredTrace(turn: Pick<StoredTurn, 'steps' | 'reasoning'>): TraceItem[] {
  const steps = [...(turn.steps ?? [])].sort((a, b) => a.i - b.i);
  const reasoning = (turn.reasoning ?? []).filter((r) => r && typeof r.text === 'string');
  const used = new Set<number>();
  const thinkingAfter = (after: number): TraceItem[] =>
    reasoning
      .map((r, index) => ({ r, index }))
      .filter(({ r, index }) => r.after === after && !used.has(index))
      .map(({ r, index }) => {
        used.add(index);
        return { kind: 'thinking', text: r.text } as TraceThinking;
      });

  const items: TraceItem[] = [...thinkingAfter(-1)];
  steps.forEach((s) => {
    items.push({
      kind: 'step',
      i: s.i,
      tool: s.tool,
      purpose: s.purpose ?? '',
      code: s.sql || s.query || undefined,
      status: s.error ? 'error' : 'done',
      count: storedCount(s),
      error: s.error ?? undefined,
      images: [],
      flow: `step ${s.i + 1}`,
      parallel: false,
      batchStart: false,
    });
    items.push(...thinkingAfter(s.i));
  });
  // Reasoning that names a step the store lost (a fire-and-forget insert can leave a gap) is
  // kept at the end rather than dropped.
  reasoning.forEach((r, index) => {
    if (!used.has(index)) items.push({ kind: 'thinking', text: r.text });
  });
  return items;
}

/** "Reasoning" when a stored turn has thinking but no steps. */
export const restoredSummary = (turn: Pick<StoredTurn, 'steps'>): string =>
  turn.steps && turn.steps.length ? finishedSummary(turn.steps) : 'Reasoning';
